import React, { useState, useCallback } from 'react';
import { Container } from "react-bootstrap";
import { useData } from '../../context/DataContext';
import { MaterialReactTable } from 'material-react-table';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, IconButton, Tooltip } from '@mui/material';
import ModeEditOutlineSharpIcon from '@mui/icons-material/ModeEditOutlineSharp';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddProductModal from './AddProductModal';
import EditProductModal from './EditProductModal';
import API_ENDPOINTS from '../../config/apiConfig';
import { fetchFromApi } from '../../config/api';
import Loading from '../Loading/Loading';

function Products() {
    const [addModal, setAddModal] = useState(false);
    const [editModal, setEditModal] = useState(false);
    const [editData, setEditData] = useState({});
    const [selectedArtist, setSelectedArtist] = useState("0");
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteError, setDeleteError] = useState(null);
    const [copiedId, setCopiedId] = useState(null);

    const copyLink = useCallback((id) => {
        navigator.clipboard.writeText(`https://artfrocollection.com/guest-shop.html?id=${id}`);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
    }, []);

    const { productsData, loading, error, refetch, artistData, fetchProductsData } = useData();

    if (loading) return <Loading />;
    if (error) return (
        <div style={{ padding: 24 }}>
            <p style={{ color: 'red' }}>Error: {error}</p>
            <button className="btn-primary-orange" onClick={refetch}>Retry</button>
        </div>
    );
    if (!productsData) return <div style={{ padding: 24 }}>No product data available.</div>;

    const filteredProducts = selectedArtist === "0" ? productsData : productsData.filter(p => p.artist_id === parseInt(selectedArtist));

    const activeEditModal = (row) => { setEditData(row.original); setEditModal(true); };

    const deleteRow = async (row) => {
        if (window.confirm("Are you sure you want to delete this product?")) {
            setDeleteLoading(true);
            try {
                const data = await fetchFromApi(API_ENDPOINTS.DELETE_PRODUCT, 'POST', { id: row.original.id });
                if (data.status === "success") await fetchProductsData();
            } catch (err) {
                setDeleteError("Failed to delete product. Please try again.");
            } finally {
                setDeleteLoading(false);
            }
        }
    };

    const renderRowActions = ({ row }) => (
        <Box sx={{ display: 'flex', gap: '0.5rem' }}>
            <Tooltip title="Edit">
                <IconButton size="small" onClick={() => activeEditModal(row)}>
                    <ModeEditOutlineSharpIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
                <IconButton size="small" onClick={() => deleteRow(row)} disabled={deleteLoading}>
                    <DeleteIcon fontSize="small" sx={{ color: '#ef4444' }} />
                </IconButton>
            </Tooltip>
        </Box>
    );

    const columns = [
        { accessorFn: (row, index) => index + 1, header: 'SNO', id: 'id', size: 60 },
        {
            accessorKey: 'product_image', header: 'Image',
            Cell: ({ cell }) => (
                <img src={cell.getValue()} alt="Product" style={{ width: 64, height: 64, borderRadius: 8, objectFit: 'cover' }} />
            ),
        },
        {
            accessorKey: 'product_name', header: 'Name',
            Cell: ({ cell, row }) => (
                <a
                    href={`https://artfrocollection.com/guest-shop.html?id=${row.original.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'var(--primary)', fontWeight: 500, textDecoration: 'none' }}
                    onMouseOver={e => e.currentTarget.style.textDecoration = 'underline'}
                    onMouseOut={e => e.currentTarget.style.textDecoration = 'none'}
                >
                    {cell.getValue()}
                </a>
            ),
        },
        { accessorKey: 'product_short_description', header: 'Description' },
        { accessorKey: 'image_dimensions', header: 'Dimensions' },
        {
            accessorKey: 'product_final_price', header: 'Price',
            Cell: ({ cell }) => <span style={{ fontWeight: 600, color: 'var(--primary)' }}>${cell.getValue()}</span>,
        },
        {
            accessorKey: 'is_available', header: 'Available',
            Cell: ({ cell }) => {
                const available = Number(cell.getValue()) === 1;
                return available
                    ? <span className="availability-badge available"><i className="fa-solid fa-circle-check" /> Available</span>
                    : <span className="availability-badge unavailable"><i className="fa-solid fa-circle-xmark" /> Not Available</span>;
            },
            size: 140,
        },
        {
            id: 'copy_link', header: 'Copy Link', size: 110,
            Cell: ({ row }) => {
                const id = row.original.id;
                const copied = copiedId === id;
                return (
                    <Tooltip title={copied ? 'Copied!' : 'Copy product link'}>
                        <IconButton size="small" onClick={() => copyLink(id)}>
                            <ContentCopyIcon fontSize="small" sx={{ color: copied ? '#22c55e' : 'var(--text-secondary)' }} />
                        </IconButton>
                    </Tooltip>
                );
            },
        },
        {
            id: 'actions', header: 'Actions', Cell: (props) => renderRowActions(props), size: 100,
            muiTableBodyCellProps: { sx: { position: 'sticky', right: 0, backgroundColor: '#fff', zIndex: 1 } },
            muiTableHeadCellProps: { sx: { position: 'sticky', right: 0, backgroundColor: '#fff', zIndex: 1 } },
        }
    ];

    return (
        <Container fluid className="px-0">
            <div className="page-header">
                <div>
                    <h1 className="page-title">Products</h1>
                    <p className="page-subtitle">{productsData.length} products in catalog</p>
                </div>
                <button className="btn-primary-orange" onClick={() => setAddModal(true)}>
                    <i className="fa-solid fa-plus"></i> Add Product
                </button>
            </div>

            <div className="filter-bar" style={{ marginBottom: 20 }}>
                <label style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Filter by artist:</label>
                <select
                    value={selectedArtist}
                    onChange={(e) => setSelectedArtist(e.target.value)}
                    style={{
                        padding: '7px 14px', border: '1.5px solid var(--border)', borderRadius: 'var(--radius-sm)',
                        fontSize: '0.875rem', color: 'var(--text-primary)', background: 'var(--bg-surface)',
                        outline: 'none', cursor: 'pointer',
                    }}
                >
                    <option value="0">All Artists</option>
                    {artistData.map(artist => (
                        <option key={artist.id} value={artist.id}>{artist.name}</option>
                    ))}
                </select>
                <span className="stats-chip">{filteredProducts.length} products</span>
            </div>

            <div className="table-container">
                <MaterialReactTable columns={columns} data={filteredProducts} enableSorting enablePagination />
            </div>

            {deleteError && <p style={{ color: '#ef4444', marginTop: 12, fontSize: '0.875rem' }}>{deleteError}</p>}

            {addModal && <AddProductModal show={addModal} onHide={() => setAddModal(false)} />}
            {editModal && (
                <EditProductModal
                    show={editModal}
                    editData={editData}
                    onHide={() => { setEditModal(false); setEditData({}); }}
                />
            )}
        </Container>
    );
}

export default Products;
